import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FiUser,
  FiMail,
  FiBriefcase,
  FiShield,
  FiCalendar,
  FiActivity,
  FiFileText,
  FiFolder,
  FiUsers,
  FiSettings,
  FiCheck,
  FiX,
  FiRefreshCw,
  FiBarChart2,
  FiGrid
} from 'react-icons/fi';
import { usePermissions } from '../../contexts/PermissionContext';
import config from '../../config/frontend.config';
import AxiosInstance from '../../AxiosInstance';
import './UserDashboard.css';
import globalRequestManager from '../../utils/globalRequestManager';

function UserDashboard() {
  
  const { permissions, userRole, hasPermission, loading: permissionsLoading } = usePermissions();
  const location = useLocation();
  // Try to get existing stats from localStorage
  const getExistingStats = () => {
    try {
      const savedStats = localStorage.getItem('userDashboardStats');
      if (savedStats) {
        return JSON.parse(savedStats);
      }
    } catch (error) {
      // Could not load saved stats
    }
    return {
      templates: 0,
      activities: 0,
      lastLogin: null
    };
  };

  const [userStats, setUserStats] = useState(getExistingStats());
  const [loading, setLoading] = useState(false); // Start with false to show existing data
  const [refreshing, setRefreshing] = useState(false);

  const [userData, setUserDataState] = useState(config.getUserData());
  const userId = config.getUserId();
  

  useEffect(() => {
    // Don't fetch if permissions are still loading or userId is not available
    // Also check if permissions object is populated (means permissions are loaded)
    if (permissionsLoading || !userId || !permissions || Object.keys(permissions).length === 0) {
      return;
    }
    
    // API calls on every page load/refresh
    const fetchData = async () => {
      
      // Fetch templates
      if (hasPermission('canViewTemplates')) {
        const url = `${config.api.baseURL}/${config.api.apiPath}/templates/user/${userId}`;
        
        const result = await globalRequestManager.executeRequest(
          async () => {
            const templatesResponse = await AxiosInstance.get(url);
            if (templatesResponse && templatesResponse.data && templatesResponse.data.statusCode === 200) {
              setUserStats(prev => ({
                ...prev,
                templates: templatesResponse.data.data?.length || 0
              }));
            }
            return templatesResponse;
          },
          'GET',
          url
        );
        
        if (result === null) {
        }
      }

      // Fetch activities
      if (hasPermission('canViewActivity')) {
        const url = `${config.api.baseURL}/${config.api.apiPath}/activities/user/${userId}`;
        
        const result = await globalRequestManager.executeRequest(
          async () => {
            const activitiesResponse = await AxiosInstance.get(url);
            if (activitiesResponse && activitiesResponse.data && activitiesResponse.data.statusCode === 200) {
              setUserStats(prev => ({
                ...prev,
                activities: activitiesResponse.data.data?.length || 0
              }));
            }
            return activitiesResponse;
          },
          'GET',
          url
        );
        
        if (result === null) {
        }
      }
    };
    
    fetchData();
    
    // Set up periodic refresh every 10 minutes to check for team status changes
    const intervalId = setInterval(async () => {
      try {
        const refreshedData = await config.refreshUserData();
        if (refreshedData) {
          setUserDataState(refreshedData);
          localStorage.setItem('lastUserDataRefresh', Date.now().toString());
        }
      } catch (error) {
        console.error('Error during periodic refresh:', error);
      }
    }, 10 * 60 * 1000); // 10 minutes
    
    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, [userId, permissionsLoading, permissions]);
  
  // Simple navigation refresh
  useEffect(() => {
    // Don't fetch if permissions are still loading or userId is not available
    // Also check if permissions object is populated (means permissions are loaded)
    if (permissionsLoading || !userId || !permissions || Object.keys(permissions).length === 0) {
      return;
    }
    
    if (location.pathname === '/' || location.pathname === '/dashboard') {
      
      // Simple API calls on navigation
      const fetchDataOnNavigation = async () => {
        // Fetch templates
        if (hasPermission('canViewTemplates')) {
          try {
            const templatesResponse = await AxiosInstance.get(
              `${config.api.baseURL}/${config.api.apiPath}/templates/user/${userId}`
            );
            if (templatesResponse && templatesResponse.data && templatesResponse.data.statusCode === 200) {
              setUserStats(prev => ({
                ...prev,
                templates: templatesResponse.data.data?.length || 0
              }));
            }
          } catch (error) {
            console.error('❌ Templates error on navigation:', error);
          }
        }

        // Fetch activities
        if (hasPermission('canViewActivity')) {
          try {
            const activitiesResponse = await AxiosInstance.get(
              `${config.api.baseURL}/${config.api.apiPath}/activities/user/${userId}`
            );
            if (activitiesResponse && activitiesResponse.data && activitiesResponse.data.statusCode === 200) {
              setUserStats(prev => ({
                ...prev,
                activities: activitiesResponse.data.data?.length || 0
              }));
            }
          } catch (error) {
            console.error('❌ Activities error on navigation:', error);
          }
        }
      };
      
      fetchDataOnNavigation();
    }
  }, [location.pathname, userId, permissionsLoading, permissions]);
  
  // Listen for user data refresh events
  useEffect(() => {
    const handleUserDataRefreshed = (event) => {
      setUserDataState(event.detail);
    };
    
    window.addEventListener('userDataRefreshed', handleUserDataRefreshed);
    
    return () => {
      window.removeEventListener('userDataRefreshed', handleUserDataRefreshed);
    };
  }, []);

  const fetchUserStats = async () => {
    try {
      setLoading(true);
      
      // Fetch user's template count
      if (hasPermission('canViewTemplates')) {
        const templatesResponse = await AxiosInstance.get(
          `${config.api.baseURL}/${config.api.apiPath}/templates/user/${userId}`
        );
        if (templatesResponse && templatesResponse.data && templatesResponse.data.statusCode === 200) {
          setUserStats(prev => ({
            ...prev,
            templates: templatesResponse.data.data?.length || 0
          }));
        }
      }

      // Fetch user's activity count
      if (hasPermission('canViewActivity')) {
        const activitiesResponse = await AxiosInstance.get(
          `${config.api.baseURL}/${config.api.apiPath}/activities/user/${userId}`
        );
        if (activitiesResponse && activitiesResponse.data && activitiesResponse.data.statusCode === 200) {
          setUserStats(prev => ({
            ...prev,
            activities: activitiesResponse.data.data?.length || 0
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    
    try {
      // Refresh user data from server
      const refreshedUserData = await config.refreshUserData();
      
      // Update local state with fresh data
      if (refreshedUserData) {
        setUserDataState(refreshedUserData);
      }
      
      // Refresh user stats
      await fetchUserStats();
    } catch (error) {
      console.error('Error during refresh:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Always show the header, even if userData is loading
  const displayName = `${userData?.FirstName} ${userData?.LastName}` || 'User';

  return (
    <div className="user-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h1 className="dashboard-title">
            Welcome back, {displayName}!
          </h1>
          <p className="dashboard-subtitle">
            Here's what's happening with your account today
          </p>
        </div>
        <button 
          className={`refresh-btn ${refreshing ? 'refreshing' : ''}`}
          onClick={handleRefresh}
          disabled={refreshing}
          title="Refresh Dashboard"
        >
          {refreshing ? (
            <div className="spinning">↻</div>
          ) : (
            <div>↻</div>
          )}
        </button>
      </div>

      <div className="dashboard-grid">
        {/* Your Activity Card */}
        <div className="dashboard-card activity-card">
          <div className="card-header">
            <h3>
              <FiBarChart2 className="card-icon-ud" />
              Your Activity
            </h3>
          </div>
          <div className="card-content-ud">
            {loading || !userData ? (
              <div className="loading-state">
                <FiRefreshCw className="loading-spinner" />
                <span>Loading stats...</span>
              </div>
            ) : (
              <div className="activity-stats">
                <div className="stat-item">
                  <div className="stat-icon purple">
                    <FiFolder />
                  </div>
                  <div className="stat-info">
                    <span className="stat-number">{userStats.templates}</span>
                    <span className="stat-label">Templates Created</span>
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-icon purple">
                    <FiFileText />
                  </div>
                  <div className="stat-info">
                    <span className="stat-number">{userStats.activities}</span>
                    <span className="stat-label">Activities Logged</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions Card */}
        <div className="dashboard-card actions-card">
          <div className="card-header">
            <h3>
              <FiGrid className="card-icon-ud" />
              Quick Actions
            </h3>
          </div>
          <div className="card-content-ud">
            <div className="quick-actions">
              {hasPermission('canViewTemplates') && (
                <Link to="/projects" className="action-link">
                  <FiFolder className="action-icon" />
                  <span>View Projects</span>
                </Link>
              )}
              {hasPermission('canViewSettings') && (
                <Link to="/settings" className="action-link">
                  <FiSettings className="action-icon" />
                  <span>Account Settings</span>
                </Link>
              )}
              {hasPermission('canViewActivity') && (
                <Link to="/activity" className="action-link">
                  <FiActivity className="action-icon" />
                  <span>View Activity</span>
                </Link>
              )}
              {hasPermission('canViewLibrary') && (
                <Link to="/library" className="action-link">
                  <FiFileText className="action-icon" />
                  <span>Browse Library</span>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Your Permissions Card */}
        <div className="dashboard-card permissions-card">
          <div className="card-header">
            <h3>
              <FiCheck className="card-icon-ud" />
              Your Permissions
            </h3>
          </div>
          <div className="card-content-ud">
            <div className="permissions-list">
              {permissions && Object.entries(permissions).map(([permission, hasAccess]) => (
                <div key={permission} className="permission-item">
                  <FiCheck className={`permission-icon ${hasAccess ? 'granted' : 'denied'}`} />
                  <span className={`permission-name ${hasAccess ? 'granted' : 'denied'}`}>
                    {permission.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserDashboard;
